import type { AdminUserListResponse, AdminUserResponse } from '@inflip/contracts/admin';
import { AuthProvider, SOCIAL_PLATFORM_LABELS, UserRole } from '@inflip/shared';
import { useNavigate } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useState } from 'react';

import { DataTable } from '../components/data-table';
import { Select } from '../components/select';
import {
  Button,
  Chip,
  EmptyState,
  ErrorBanner,
  LoadingRow,
  PageHeader,
  inputCls,
} from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import { formatDateTime } from '../lib/time-zone';
import { useListViewState } from '../lib/use-list-view-state';

const ROLE_TEXT: Record<UserRole, string> = {
  USER: 'text-muted-foreground',
  ADMIN: 'text-blue-600',
  SUPER_ADMIN: 'text-brand',
};

const PROVIDER_LABELS: Record<AuthProvider, string> = {
  LOCAL: '이메일',
  GOOGLE: '구글',
  APPLE: '애플',
  KAKAO: '카카오',
};

const STATUS_CHIPS = [
  { key: 'ALL', label: '전체' },
  { key: 'ACTIVE', label: '활성' },
  { key: 'SUSPENDED', label: '정지' },
  { key: 'WITHDRAWN', label: '탈퇴' },
] as const;

const SORT_OPTIONS = [
  { value: 'createdAt', label: '가입일순' },
  { value: 'lastLoginAt', label: '최근 로그인순' },
  { value: 'orderCount', label: '주문 많은순' },
] as const;

const PAGE_SIZE = 20;

const COLUMNS: ColumnDef<AdminUserResponse>[] = [
  {
    id: 'user',
    header: '회원',
    size: 280,
    cell: ({ row }) => (
      <div className="flex items-center gap-2.5">
        {row.original.avatarUrl ? (
          <img
            src={row.original.avatarUrl}
            alt=""
            className="h-8 w-8 shrink-0 rounded-full object-cover"
          />
        ) : (
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-muted text-xs font-bold text-muted-foreground">
            {row.original.name.slice(0, 1)}
          </span>
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-1">
            <span className="truncate font-semibold">{row.original.name}</span>
            {row.original.verifiedPlatforms.length > 0 && (
              <VerifiedIcon
                label={`인증된 인플루언서 · ${row.original.verifiedPlatforms
                  .map((p) => SOCIAL_PLATFORM_LABELS[p])
                  .join(', ')}`}
              />
            )}
          </div>
          <div className="truncate text-xs text-muted-foreground">{row.original.email}</div>
        </div>
      </div>
    ),
  },
  {
    id: 'status',
    header: '상태',
    size: 80,
    cell: ({ row }) =>
      row.original.withdrawnAt ? (
        <span className="text-xs font-bold text-muted-foreground">
          {row.original.anonymizedAt ? '탈퇴 · 파기' : '탈퇴'}
        </span>
      ) : row.original.suspendedAt ? (
        <span className="text-xs font-bold text-rose-600">정지</span>
      ) : (
        <span className="text-xs font-bold text-emerald-600">활성</span>
      ),
  },
  {
    id: 'role',
    header: '역할',
    size: 140,
    cell: ({ row }) => (
      <span className={`text-xs font-bold ${ROLE_TEXT[row.original.role]}`}>
        {row.original.role}
        {row.original.mfaEnrolled && ' · MFA'}
      </span>
    ),
  },
  {
    id: 'provider',
    header: '가입 경로',
    size: 90,
    cell: ({ row }) =>
      `${PROVIDER_LABELS[row.original.provider]}${row.original.emailVerified ? ' ✓' : ''}`,
    meta: { tdClass: 'text-xs text-muted-foreground' },
  },
  {
    id: 'activity',
    header: '활동',
    size: 120,
    cell: ({ row }) => `주문 ${row.original.orderCount} · 판매 ${row.original.listingCount}`,
    meta: { tdClass: 'text-xs text-muted-foreground' },
  },
  {
    id: 'lastLoginAt',
    header: '최근 로그인',
    cell: ({ row }) => (row.original.lastLoginAt ? formatDateTime(row.original.lastLoginAt) : '—'),
    meta: { tdClass: 'text-xs text-muted-foreground' },
  },
  {
    id: 'createdAt',
    header: '가입일',
    cell: ({ row }) => formatDateTime(row.original.createdAt),
    meta: { tdClass: 'text-xs text-muted-foreground' },
  },
];

/** 인증된 인플루언서 표시 — SNS 채널 인증을 1개 이상 마친 회원 */
function VerifiedIcon({ label }: { label: string }) {
  return (
    <svg
      viewBox="0 0 20 20"
      className="h-4 w-4 shrink-0 text-brand"
      fill="currentColor"
      role="img"
      aria-label={label}
    >
      <title>{label}</title>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 1.5l2.1 1.6 2.6-.2.8 2.5 2.2 1.4-.9 2.5.9 2.5-2.2 1.4-.8 2.5-2.6-.2L10 17.1l-2.1-1.6-2.6.2-.8-2.5-2.2-1.4.9-2.5-.9-2.5 2.2-1.4.8-2.5 2.6.2L10 1.5zm3.3 6.6a.75.75 0 00-1.1-1l-3 3.3-1.4-1.4a.75.75 0 10-1.1 1.1l2 2a.75.75 0 001.1 0l3.5-4z"
      />
    </svg>
  );
}

export function UsersPage() {
  const { session } = useAdminAuth();
  const navigate = useNavigate();
  // 목록 상태는 URL 에 — 상세에서 뒤로 나와도 보던 페이지·필터가 유지된다
  const [status, setStatus] = useListViewState<(typeof STATUS_CHIPS)[number]['key']>(
    'status',
    'ALL',
  );
  const [searchTerm, setSearchTerm] = useListViewState('q', '');
  const [q, setQ] = useState(searchTerm);
  const [role, setRole] = useListViewState<UserRole | ''>('role', '');
  const [provider, setProvider] = useListViewState<AuthProvider | ''>('provider', '');
  const [sort, setSort] = useListViewState<(typeof SORT_OPTIONS)[number]['value']>(
    'sort',
    'createdAt',
  );
  const [page, setPage] = useListViewState('page', 1);
  const [result, setResult] = useState<AdminUserListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    setLoading(true);
    api
      .listUsers(session.accessToken, {
        page,
        pageSize: PAGE_SIZE,
        sort,
        ...(searchTerm ? { q: searchTerm } : {}),
        ...(role ? { role } : {}),
        ...(provider ? { provider } : {}),
        ...(status !== 'ALL' ? { status } : {}),
      })
      .then((r) => !cancelled && setResult(r))
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : String(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [session, page, sort, searchTerm, role, provider, status]);

  if (!session) return null;

  const rows = result?.items ?? [];

  return (
    <main className="w-full px-6 py-8">
      <PageHeader
        title="회원 관리"
        description={
          result ? `전체 ${result.total.toLocaleString('ko-KR')}명` : '가입한 회원을 관리해요'
        }
        actions={
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              setSearchTerm(q);
            }}
          >
            <input
              className={`w-64 ${inputCls}`}
              placeholder="이메일 / 이름 검색"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <Button type="submit">검색</Button>
          </form>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        {STATUS_CHIPS.map((c) => (
          <Chip
            key={c.key}
            active={status === c.key}
            onClick={() => {
              setPage(1);
              setStatus(c.key);
            }}
          >
            {c.label}
          </Chip>
        ))}
        <div className="ml-auto flex gap-2">
          <Select
            label="역할"
            className="w-40"
            value={role}
            options={[
              { value: '', label: '역할 전체' },
              ...Object.values(UserRole).map((r) => ({ value: r, label: r })),
            ]}
            onValueChange={(v) => {
              setPage(1);
              setRole(v as UserRole | '');
            }}
          />
          <Select
            label="가입 경로"
            className="w-40"
            value={provider}
            options={[
              { value: '', label: '가입 경로 전체' },
              ...Object.values(AuthProvider).map((p) => ({ value: p, label: PROVIDER_LABELS[p] })),
            ]}
            onValueChange={(v) => {
              setPage(1);
              setProvider(v as AuthProvider | '');
            }}
          />
          <Select
            label="정렬"
            className="w-40"
            value={sort}
            options={SORT_OPTIONS}
            onValueChange={(v) => {
              setPage(1);
              setSort(v as typeof sort);
            }}
          />
        </div>
      </div>

      {error && <ErrorBanner message={error} />}
      {loading && !result && <LoadingRow />}

      {result && rows.length === 0 ? (
        <EmptyState message="결과가 없어요." />
      ) : (
        <DataTable
          data={rows}
          columns={COLUMNS}
          getRowId={(u) => u.id}
          onRowClick={(u) => void navigate({ to: '/users/$userId', params: { userId: u.id } })}
        />
      )}

      {result && result.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            이전
          </Button>
          <span className="text-sm tabular-nums text-muted-foreground">
            {result.page} / {result.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= result.totalPages}
            onClick={() => setPage(page + 1)}
          >
            다음
          </Button>
        </div>
      )}
    </main>
  );
}
